import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { WORK_CENTERS } from "@/constants/testIds";

// El icono default de Leaflet apunta a rutas relativas que el bundler no resuelve; se
// reemplaza una sola vez por los assets ya empaquetados por el propio paquete "leaflet".
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow });

// Centro de México como punto de partida cuando aún no hay coordenadas capturadas — se
// recentra en cuanto el usuario arrastra el marcador, hace clic, o usa "Mi ubicación actual".
const DEFAULT_CENTER = [19.4326, -99.1332];

// Selector visual de ubicación (OpenStreetMap vía Leaflet, sin API key). Arrastrar el
// marcador o hacer clic en el mapa actualiza lat/lng; el círculo muestra el radio de
// tolerancia configurado, para confirmar de un vistazo que la geolocalización quedará bien
// calibrada en vez de depender solo de escribir coordenadas a mano.
export function WorkCenterMapPicker({ latitude, longitude, radiusMeters, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const hasCoords = latitude !== "" && longitude !== "" && Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude));
    const start = hasCoords ? [Number(latitude), Number(longitude)] : DEFAULT_CENTER;

    const map = L.map(containerRef.current).setView(start, hasCoords ? 16 : 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    const marker = L.marker(start, { draggable: true }).addTo(map);
    const circle = L.circle(start, { radius: Number(radiusMeters) || 150, color: "#0E4F4F", weight: 1.5, fillOpacity: 0.12 }).addTo(map);

    const applyPosition = (lat, lng) => {
      marker.setLatLng([lat, lng]);
      circle.setLatLng([lat, lng]);
      onChangeRef.current(lat, lng);
    };

    marker.on("dragend", () => {
      const pos = marker.getLatLng();
      applyPosition(pos.lat, pos.lng);
    });
    map.on("click", (e) => applyPosition(e.latlng.lat, e.latlng.lng));

    mapRef.current = map;
    markerRef.current = marker;
    circleRef.current = circle;

    // Los mapas de Leaflet montados dentro de un diálogo a veces miden el contenedor antes de
    // que termine la animación de apertura y quedan con tiles a medio cargar (grises).
    const resizeTimer = setTimeout(() => map.invalidateSize(), 150);

    return () => {
      clearTimeout(resizeTimer);
      map.remove();
    };
    // Se crea una sola vez; los cambios posteriores de coordenadas/radio se sincronizan abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recentra el mapa cuando lat/lng cambian desde fuera (inputs numéricos o "Mi ubicación actual").
  useEffect(() => {
    if (!mapRef.current || !markerRef.current) return;
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const current = markerRef.current.getLatLng();
    if (Math.abs(current.lat - lat) > 1e-9 || Math.abs(current.lng - lng) > 1e-9) {
      markerRef.current.setLatLng([lat, lng]);
      circleRef.current?.setLatLng([lat, lng]);
      mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 15));
    }
  }, [latitude, longitude]);

  useEffect(() => {
    circleRef.current?.setRadius(Number(radiusMeters) || 150);
  }, [radiusMeters]);

  return (
    <div>
      <div data-testid={WORK_CENTERS.mapPicker} ref={containerRef} style={{ height: 260, borderRadius: 12 }} className="border border-petroleum/20 overflow-hidden" />
      <p className="text-xs text-charcoal/50 mt-1.5">Arrastra el marcador o haz clic en el mapa para ajustar la ubicación exacta.</p>
    </div>
  );
}
